import { useState, useEffect } from "react";
import {
  X,
  Loader,
  Calendar,
  User,
  MapPin,
  FileText,
  Clock,
  Target,
  ChevronDown,
  ChevronUp,
  Plus,
} from "lucide-react";
import apiService from "../services/apiService";
import { RECON } from "../services/apiConstant";
import { useAuth } from "../providers/AuthProvider";
import { toast } from "sonner";

const ReconRequestsViewModal = ({ vehicle, onClose }) => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quotesByRequest, setQuotesByRequest] = useState({});
  const [quoteItemsData, setQuoteItemsData] = useState({}); // Store quote items by quote_id
  const [expandedQuotes, setExpandedQuotes] = useState({});
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [quoteStep, setQuoteStep] = useState(1); // 1 = provider/ETA, 2 = items
  const [quoteForm, setQuoteForm] = useState({
    provider: "",
    eta: "",
  });
  const [quoteItems, setQuoteItems] = useState([
    { description: "", price: "", type: "Parts" },
  ]);
  const [createdQuoteId, setCreatedQuoteId] = useState(null);
  const [submittingQuote, setSubmittingQuote] = useState(false);

  // Provider dropdown states (similar to EditVehicleForm)
  const [providerQuery, setProviderQuery] = useState("");
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [allStages, setAllStages] = useState([]);
  const [isProviderDropdownOpen, setIsProviderDropdownOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  // Quote status editing states
  const [editingQuoteStatus, setEditingQuoteStatus] = useState({}); // {quoteId: newStatus}
  const [updatingQuoteStatus, setUpdatingQuoteStatus] = useState({}); // {quoteId: boolean}

  // Request status editing states
  const [editingStatus, setEditingStatus] = useState({}); // {requestId: newStatus}
  const [updatingStatus, setUpdatingStatus] = useState({}); // {requestId: boolean}

  // Quote status options
  const quoteStatusOptions = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "in progress", label: "In Progress" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
    { value: "paid", label: "Paid" },
  ];

  // Status options for request editing
  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "quote", label: "Quote" },
    { value: "approved", label: "Approved" },
    { value: "in progress", label: "In Progress" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
  ];

  useEffect(() => {
    const fetchReconRequests = async () => {
      if (!vehicle?.vin) return;

      setLoading(true);
      setError(null);

      try {
        const response = await apiService.get(
          `${RECON.reconRequests}?vin=${vehicle.vin}`
        );
        if (response.success) {
          setRequests(response.recon_requests || []);
        } else {
          setError("Failed to fetch recon requests");
        }
      } catch (error) {
        console.error("❌ Error fetching recon requests:", error);
        setError(
          error.response?.data?.detail ||
          error.message ||
          "Failed to fetch requests"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchReconRequests();
  }, [vehicle?.vin]);

  // Helper function to fetch quote items
  const fetchQuoteItems = async (quotes) => {
    try {
      const itemsPromises = quotes.map(async (quote) => {
        try {
          const url = `${RECON.quoteItems}?quote_id=${quote.quote_id}`;
          const response = await apiService.get(url);

          let items = [];
          if (response.success && Array.isArray(response.items)) {
            items = response.items;
          } else if (Array.isArray(response)) {
            items = response;
          }

          return {
            quoteId: quote.quote_id,
            items: items,
          };
        } catch (error) {
          console.error(
            `Error fetching items for quote ${quote.quote_id}:`,
            error
          );
          return {
            quoteId: quote.quote_id,
            items: [],
          };
        }
      });

      const results = await Promise.all(itemsPromises);

      // Update quoteItemsData with all fetched items
      const newItemsData = {};
      results.forEach((result) => {
        if (result && result.quoteId) {
          newItemsData[result.quoteId] = result.items;
        }
      });

      setQuoteItemsData(newItemsData);
    } catch (error) {
      console.error("Error fetching quote items:", error);
    }
  };

  // Helper function to calculate price from quote items
  const calculateQuotePrice = (quoteId) => {
    const items = quoteItemsData[quoteId] || [];
    if (items.length === 0) {
      return 0;
    }
    const total = items.reduce(
      (sum, item) => sum + (parseFloat(item.price) || 0),
      0
    );
    return total;
  };

  // Fetch quotes for all requests after requests are loaded
  useEffect(() => {
    const fetchQuotes = async () => {
      if (!requests.length) return;
      const quotesMap = {};
      const allQuotes = [];

      await Promise.all(
        requests.map(async (req) => {
          try {
            const response = await apiService.get(
              `${RECON.quotes}?request_id=${req.request_id || req.id}`
            );
            let quotes = [];
            if (response.success && response.quotes) {
              quotes = response.quotes;
              quotesMap[req.request_id || req.id] = quotes;
            } else if (Array.isArray(response)) {
              // Handle case where API returns quotes directly as array
              quotes = response;
              quotesMap[req.request_id || req.id] = quotes;
            } else {
              quotesMap[req.request_id || req.id] = [];
            }
            allQuotes.push(...quotes);
          } catch (error) {
            console.error(
              `❌ Error fetching quotes for request ${req.request_id || req.id
              }:`,
              error
            );
            quotesMap[req.request_id || req.id] = [];
          }
        })
      );

      setQuotesByRequest(quotesMap);

      // Fetch quote items for all quotes
      if (allQuotes.length > 0) {
        await fetchQuoteItems(allQuotes);
      }
    };

    fetchQuotes();
  }, [requests]);

  // Fetch possible stages for provider options (filtered by stage = 'Offsite')
  useEffect(() => {
    const fetchPossibleStages = async () => {
      setLoadingProviders(true);
      try {
        // Filter by stage = 'Offsite' to get only offsite providers
        const response = await apiService.get(
          RECON.possibleStages
        );
        console.log(
          "✅ Possible stages fetched for providers (Offsite only):",
          response
        );
        if (response.success && response.stages) {
          setAllStages(response.stages);
        }
      } catch (error) {
        console.error("❌ Error fetching possible stages:", error);
      } finally {
        setLoadingProviders(false);
      }
    };

    fetchPossibleStages();
  }, []);

  // Filter providers based on query (similar to EditVehicleForm)
  useEffect(() => {
    if (!allStages.length) {
      setFilteredProviders([]);
      return;
    }

    // Extract unique stage3 values and filter by query
    const uniqueStage3Values = [
      ...new Set(
        allStages
          .filter((stage) => stage.stage3) // Only include stages with stage3
          .map((stage) => ({
            id: stage.id,
            name: stage.stage3,
            lot: stage.lot,
            stage: stage.stage,
            stage2: stage.stage2,
          }))
      ),
    ];

    if (providerQuery.trim().length === 0) {
      // Show all options when no search query
      setFilteredProviders(uniqueStage3Values);
    } else {
      // Filter by search query
      const filtered = uniqueStage3Values.filter(
        (provider) =>
          provider.name.toLowerCase().includes(providerQuery.toLowerCase()) ||
          provider.lot.toLowerCase().includes(providerQuery.toLowerCase()) ||
          provider.stage.toLowerCase().includes(providerQuery.toLowerCase()) ||
          provider.stage2.toLowerCase().includes(providerQuery.toLowerCase())
      );
      setFilteredProviders(filtered);
    }
  }, [providerQuery, allStages]);

  // Generate placeholder ETA (3-7 days from created date)
  const generatePlaceholderETA = (createdAt) => {
    if (!createdAt) return null;
    try {
      const created = new Date(createdAt);
      const daysToAdd = Math.floor(Math.random() * 5) + 3; // Random 3-7 days
      const eta = new Date(created);
      eta.setDate(created.getDate() + daysToAdd);
      return eta.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "TBD";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const toggleQuotesExpanded = (requestId) => {
    setExpandedQuotes((prev) => ({
      ...prev,
      [requestId]: !prev[requestId],
    }));
  };

  const handleCreateQuote = (requestId) => {
    setSelectedRequestId(requestId);
    setQuoteStep(1);
    setQuoteForm({
      provider: "",
      eta: new Date().toISOString().split("T")[0], // Default to today
    });
    setQuoteItems([{ description: "", price: "", type: "Parts" }]);
    setCreatedQuoteId(null);
    // Reset provider dropdown state
    setProviderQuery("");
    setSelectedProvider(null);
    setIsProviderDropdownOpen(false);
    setShowQuoteModal(true);
  };

  const handleCloseQuoteModal = () => {
    setShowQuoteModal(false);
    setSelectedRequestId(null);
    setQuoteStep(1);
    setQuoteForm({
      provider: "",
      eta: "",
    });
    setQuoteItems([{ description: "", price: "", type: "Parts" }]);
    setCreatedQuoteId(null);
    // Reset provider dropdown state
    setProviderQuery("");
    setSelectedProvider(null);
    setIsProviderDropdownOpen(false);
  };

  const handleQuoteFormChange = (field, value) => {
    setQuoteForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleQuoteItemChange = (index, field, value) => {
    const updatedItems = [...quoteItems];
    updatedItems[index][field] = value;
    setQuoteItems(updatedItems);
  };

  const handleAddQuoteItem = () => {
    setQuoteItems([
      ...quoteItems,
      { description: "", price: "", type: "Parts" },
    ]);
  };

  const handleRemoveQuoteItem = (index) => {
    if (quoteItems.length === 1) return; // Keep at least one item
    setQuoteItems(quoteItems.filter((_, i) => i !== index));
  };

  // Step 1: Create quote with provider and ETA
  const handleCreateQuoteStep1 = async () => {
    // Validate required fields
    if (!selectedProvider) {
      toast.error("Please select a provider.");
      return;
    }

    if (!quoteForm.eta) {
      toast.error("Please select an ETA date.");
      return;
    }

    setSubmittingQuote(true);
    try {
      const payload = {
        request_id: selectedRequestId,
        provider: selectedProvider.name, // Use selected provider name
        eta: quoteForm.eta,
        status: "approved",
      };

      const response = await apiService.post(RECON.quotes, payload);

      if (response.success) {
        const quoteId = response.quote?.quote_id || response.quote_id;
        setCreatedQuoteId(quoteId);
        setQuoteStep(2);
      } else {
        toast.error("Failed to create quote: " + (response.message || "Unknown error"));
      }
    } catch (error) {
      console.error("❌ Error creating quote:", error);
      toast.error(
        "Error creating quote: " +
        (error.response?.data?.detail || error.message || "Unknown error")
      );
    } finally {
      setSubmittingQuote(false);
    }
  };

  // Step 2: Add quote items
  const handleSubmitQuoteItems = async () => {
    if (!createdQuoteId) return;

    // Validate all items
    const invalidItems = quoteItems.filter(
      (item) =>
        !item.description?.trim() ||
        !item.price ||
        isNaN(item.price) ||
        Number(item.price) < 0
    );

    if (invalidItems.length > 0) {
      toast.error("Please fill in all item descriptions and valid prices (>= 0).");
      return;
    }

    setSubmittingQuote(true);
    try {
      // Create all quote items
      const promises = quoteItems.map((item) =>
        apiService.post(RECON.quoteItems, {
          quote_id: createdQuoteId,
          description: `${item.type}: ${item.description}`, // Include type in description
          price: Number(item.price),
        })
      );

      const results = await Promise.all(promises);
      const allSuccess = results.every((r) => r.success);

      if (allSuccess) {
        toast.success(`Quote created successfully with ${quoteItems.length} item(s)!`);
        handleCloseQuoteModal();

        // Refresh quotes for this request
        const quotesResponse = await apiService.get(
          `${RECON.quotes}?request_id=${selectedRequestId}`
        );
        let quotes = [];
        if (quotesResponse.success && quotesResponse.quotes) {
          quotes = quotesResponse.quotes;
        } else if (Array.isArray(quotesResponse)) {
          quotes = quotesResponse;
        }

        setQuotesByRequest((prev) => ({
          ...prev,
          [selectedRequestId]: quotes,
        }));

        // Fetch quote items for the refreshed quotes
        if (quotes.length > 0) {
          await fetchQuoteItems(quotes);
        }
      } else {
        toast.error("Some items failed to create. Please try again.");
      }
    } catch (error) {
      console.error("❌ Error creating quote items:", error);
      toast.error(
        "Error creating quote items: " +
        (error.response?.data?.detail || error.message || "Unknown error")
      );
    } finally {
      setSubmittingQuote(false);
    }
  };

  // Quote status editing functions
  const handleQuoteStatusEdit = (quoteId, currentStatus) => {
    setEditingQuoteStatus((prev) => ({
      ...prev,
      [quoteId]: currentStatus,
    }));
  };

  const handleQuoteStatusUpdate = async (quoteId, newStatus) => {
    try {
      setUpdatingQuoteStatus((prev) => ({ ...prev, [quoteId]: true }));

      const response = await apiService.patch(`${RECON.quotes}/${quoteId}`, {
        status: newStatus,
        changed_by: user?.email || "unknown@user.com",
      });

      if (response.success) {
        // Update the quotes in state
        setQuotesByRequest((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((requestId) => {
            updated[requestId] = updated[requestId].map((quote) =>
              quote.quote_id === quoteId
                ? { ...quote, status: newStatus }
                : quote
            );
          });
          return updated;
        });

        // Clear editing state
        setEditingQuoteStatus((prev) => {
          const updated = { ...prev };
          delete updated[quoteId];
          return updated;
        });
      }
    } catch (error) {
      console.error("Error updating quote status:", error);
      let errorMessage = "Unknown error";
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string'
          ? error.response.data.detail
          : JSON.stringify(error.response.data.detail);
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error("Failed to update quote status: " + errorMessage);
    } finally {
      setUpdatingQuoteStatus((prev) => ({ ...prev, [quoteId]: false }));
    }
  };

  const handleQuoteStatusCancel = (quoteId) => {
    setEditingQuoteStatus((prev) => {
      const updated = { ...prev };
      delete updated[quoteId];
      return updated;
    });
  };

  // Request status editing functions
  const handleStatusEdit = (requestId, currentStatus) => {
    setEditingStatus((prev) => ({
      ...prev,
      [requestId]: currentStatus,
    }));
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    if (!requestId || !newStatus) return;

    setUpdatingStatus((prev) => ({
      ...prev,
      [requestId]: true,
    }));

    try {
      const response = await apiService.patch(
        `${RECON.reconRequests}/${requestId}`,
        {
          status: newStatus,
          changed_by: user?.email || "unknown@user.com",
        }
      );

      if (response.success) {
        // Update the request in the local state
        setRequests((prev) =>
          prev.map((req) =>
            req.request_id === requestId ? { ...req, status: newStatus } : req
          )
        );

        // Clear editing state
        setEditingStatus((prev) => {
          const newState = { ...prev };
          delete newState[requestId];
          return newState;
        });

        console.log("✅ Request status updated successfully");
      } else {
        throw new Error(response.message || "Failed to update request status");
      }
    } catch (error) {
      console.error("❌ Error updating request status:", error);
      let errorMessage = "Unknown error";
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string'
          ? error.response.data.detail
          : JSON.stringify(error.response.data.detail);
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(`Failed to update request status: ${errorMessage}`);
    } finally {
      setUpdatingStatus((prev) => ({
        ...prev,
        [requestId]: false,
      }));
    }
  };

  const handleStatusCancel = (requestId) => {
    setEditingStatus((prev) => {
      const newState = { ...prev };
      delete newState[requestId];
      return newState;
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      approved: "bg-green-100 text-green-800 border-green-200",
      rejected: "bg-red-100 text-red-800 border-red-200",
      completed: "bg-blue-100 text-blue-800 border-blue-200",
    };
    return (
      colors[status?.toLowerCase()] ||
      "bg-gray-100 text-gray-800 border-gray-200"
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Recon Requests
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              VIN: {vehicle.vin} • {vehicle.vehicle_year} {vehicle.make}{" "}
              {vehicle.model}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader size={32} className="animate-spin text-blue-500" />
              <span className="ml-3 text-gray-600">
                Loading recon requests...
              </span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-500 mb-2">
                <FileText size={48} className="mx-auto mb-4 opacity-50" />
              </div>
              <h3 className="text-lg font-medium text-red-800 mb-2">
                Error Loading Requests
              </h3>
              <p className="text-red-600">{error}</p>
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-2">
                <FileText size={48} className="mx-auto mb-4 opacity-50" />
              </div>
              <h3 className="text-lg font-medium text-gray-800 mb-2">
                No Recon Requests
              </h3>
              <p className="text-gray-600">
                This vehicle has no recon requests yet.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-800">
                  {requests.length} Request{requests.length !== 1 ? "s" : ""}{" "}
                  Found
                </h3>
              </div>

              {/* Requests List */}
              <div className="grid gap-4">
                {requests.map((request) => (
                  <div
                    key={request.id || request.request_id}
                    className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-gray-300 transition-colors group"
                  >
                    {/* Request Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {editingStatus[request.request_id] ? (
                            <div className="flex items-center gap-2">
                              <select
                                value={editingStatus[request.request_id]}
                                onChange={(e) =>
                                  setEditingStatus((prev) => ({
                                    ...prev,
                                    [request.request_id]: e.target.value,
                                  }))
                                }
                                className="px-2 py-1 text-xs border rounded"
                                disabled={updatingStatus[request.request_id]}
                              >
                                {statusOptions.map((option) => (
                                  <option
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              <button
                                onClick={() =>
                                  handleStatusUpdate(
                                    request.request_id,
                                    editingStatus[request.request_id]
                                  )
                                }
                                disabled={updatingStatus[request.request_id]}
                                className="text-green-600 hover:text-green-700 text-xs px-2 py-1 border border-green-300 rounded disabled:opacity-50"
                              >
                                {updatingStatus[request.request_id]
                                  ? "Saving..."
                                  : "Save"}
                              </button>
                              <button
                                onClick={() =>
                                  handleStatusCancel(request.request_id)
                                }
                                disabled={updatingStatus[request.request_id]}
                                className="text-gray-600 hover:text-gray-700 text-xs px-2 py-1 border border-gray-300 rounded disabled:opacity-50"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(
                                  request.status
                                )}`}
                              >
                                {request.status || "Unknown"}
                              </span>
                              <button
                                onClick={() =>
                                  handleStatusEdit(
                                    request.request_id,
                                    request.status
                                  )
                                }
                                className="text-blue-600 hover:text-blue-700 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                Edit
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Description */}
                        {request.description && (
                          <p className="text-gray-800 mb-2 whitespace-pre-wrap">
                            {request.description}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Request Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                      {/* Provider */}
                      {request.provider && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin size={14} className="text-blue-500" />
                          <span className="font-medium">Provider:</span>
                          <span>{request.provider}</span>
                        </div>
                      )}

                      {/* User */}
                      {request.user && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <User size={14} className="text-green-500" />
                          <span className="font-medium">Created by:</span>
                          <span>{request.user}</span>
                        </div>
                      )}

                      {/* ETA - Placeholder */}
                      <div className="flex items-center gap-2 text-gray-600">
                        <Target size={14} className="text-amber-500" />
                        <span className="font-medium">ETA:</span>
                        <span className="text-amber-600 font-medium">
                          {generatePlaceholderETA(request.created_at)}
                        </span>
                      </div>

                      {/* Created Date */}
                      {request.created_at && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar size={14} className="text-purple-500" />
                          <span className="font-medium">Created:</span>
                          <span>{formatDate(request.created_at)}</span>
                        </div>
                      )}

                      {/* Last Update */}
                      {request.last_update &&
                        request.last_update !== request.created_at && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <Clock size={14} className="text-orange-500" />
                            <span className="font-medium">Updated:</span>
                            <span>{formatDate(request.last_update)}</span>
                          </div>
                        )}

                      {/* Phase */}
                      {request.phase && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <FileText size={14} className="text-indigo-500" />
                          <span className="font-medium">Phase:</span>
                          <span>{request.phase}</span>
                        </div>
                      )}

                      {/* Stage */}
                      {request.stage && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Target size={14} className="text-teal-500" />
                          <span className="font-medium">Stage:</span>
                          <span>{request.stage}</span>
                        </div>
                      )}
                    </div>

                    {/* Quotes Section - Now at Bottom with Accordion */}
                    <div className="border-t border-gray-300 pt-3 mt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4
                          className="text-sm font-semibold text-gray-700 cursor-pointer hover:text-gray-900 transition-colors flex items-center gap-1"
                          onClick={() => {
                            if (
                              quotesByRequest[
                              request.request_id || request.id
                              ] &&
                              quotesByRequest[request.request_id || request.id]
                                .length > 0
                            ) {
                              toggleQuotesExpanded(
                                request.request_id || request.id
                              );
                            }
                          }}
                        >
                          Quotes{" "}
                          {quotesByRequest[request.request_id || request.id] &&
                            quotesByRequest[request.request_id || request.id]
                              .length > 0 && (
                              <span className="text-xs text-gray-500 ml-1">
                                (
                                {
                                  quotesByRequest[
                                    request.request_id || request.id
                                  ].length
                                }
                                )
                              </span>
                            )}
                          {quotesByRequest[request.request_id || request.id] &&
                            quotesByRequest[request.request_id || request.id]
                              .length > 0 && (
                              <span className="ml-2">
                                {expandedQuotes[
                                  request.request_id || request.id
                                ] ? (
                                  <ChevronUp
                                    size={16}
                                    className="text-gray-500"
                                  />
                                ) : (
                                  <ChevronDown
                                    size={16}
                                    className="text-gray-500"
                                  />
                                )}
                              </span>
                            )}
                        </h4>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              handleCreateQuote(
                                request.request_id || request.id
                              )
                            }
                            className="flex items-center gap-1 px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors"
                          >
                            <Plus size={14} />
                            Create Quote
                          </button>
                        </div>
                      </div>

                      {quotesByRequest[request.request_id || request.id] &&
                        quotesByRequest[request.request_id || request.id].length >
                        0 ? (
                        expandedQuotes[request.request_id || request.id] && (
                          <div className="space-y-2">
                            {quotesByRequest[
                              request.request_id || request.id
                            ].map((quote, index) => (
                              <div
                                key={quote.quote_id || quote.id || index}
                                className="border rounded p-3 bg-white"
                              >
                                <div className="space-y-2">
                                  {/* Quote Header */}
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-medium text-gray-600">
                                        Provider:
                                      </span>
                                      <span className="text-sm font-semibold text-gray-800">
                                        {quote.provider || "N/A"}
                                      </span>
                                    </div>
                                    {quote.status &&
                                      (editingQuoteStatus[quote.quote_id] !==
                                        undefined ? (
                                        // Editing mode - show dropdown
                                        <div className="flex items-center gap-2">
                                          <select
                                            value={
                                              editingQuoteStatus[quote.quote_id]
                                            }
                                            onChange={(e) =>
                                              setEditingQuoteStatus((prev) => ({
                                                ...prev,
                                                [quote.quote_id]:
                                                  e.target.value,
                                              }))
                                            }
                                            className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-500"
                                            disabled={
                                              updatingQuoteStatus[
                                              quote.quote_id
                                              ]
                                            }
                                          >
                                            {quoteStatusOptions.map(
                                              (option) => (
                                                <option
                                                  key={option.value}
                                                  value={option.value}
                                                >
                                                  {option.label}
                                                </option>
                                              )
                                            )}
                                          </select>
                                          <div className="flex gap-1">
                                            <button
                                              onClick={() =>
                                                handleQuoteStatusUpdate(
                                                  quote.quote_id,
                                                  editingQuoteStatus[
                                                  quote.quote_id
                                                  ]
                                                )
                                              }
                                              disabled={
                                                updatingQuoteStatus[
                                                quote.quote_id
                                                ]
                                              }
                                              className="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600 disabled:bg-gray-300"
                                            >
                                              {updatingQuoteStatus[
                                                quote.quote_id
                                              ] ? (
                                                <Loader className="animate-spin h-3 w-3" />
                                              ) : (
                                                "✓"
                                              )}
                                            </button>
                                            <button
                                              onClick={() =>
                                                handleQuoteStatusCancel(
                                                  quote.quote_id
                                                )
                                              }
                                              disabled={
                                                updatingQuoteStatus[
                                                quote.quote_id
                                                ]
                                              }
                                              className="text-xs bg-gray-500 text-white px-2 py-1 rounded hover:bg-gray-600 disabled:bg-gray-300"
                                            >
                                              ✕
                                            </button>
                                          </div>
                                        </div>
                                      ) : (
                                        // View mode - show badge with click to edit
                                        <span
                                          className={`px-2 py-1 rounded-full text-xs font-medium border cursor-pointer hover:bg-opacity-80 transition-colors ${getStatusColor(
                                            quote.status
                                          )}`}
                                          onClick={() =>
                                            handleQuoteStatusEdit(
                                              quote.quote_id,
                                              quote.status
                                            )
                                          }
                                          title="Click to edit status"
                                        >
                                          {quote.status}
                                        </span>
                                      ))}
                                  </div>

                                  {/* Quote Details */}
                                  <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                      <span className="text-xs text-gray-500">
                                        ETA:
                                      </span>
                                      <div className="font-medium">
                                        {quote.eta || "Not set"}
                                      </div>
                                    </div>
                                    <div>
                                      <span className="text-xs text-gray-500">
                                        Price:
                                      </span>
                                      <div className="font-medium text-green-600">
                                        $
                                        {calculateQuotePrice(
                                          quote.quote_id
                                        ).toFixed(2)}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Quote Description */}
                                  {quote.description && (
                                    <div>
                                      <span className="text-xs text-gray-500">
                                        Description:
                                      </span>
                                      <div className="text-sm text-gray-700">
                                        {quote.description}
                                      </div>
                                    </div>
                                  )}

                                  {/* Quote Created Date */}
                                  {quote.created_at && (
                                    <div className="text-xs text-gray-400">
                                      Created: {formatDate(quote.created_at)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )
                      ) : (
                        <div className="text-xs text-gray-400">
                          No quotes yet.
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* Quote Creation Modal - Two Step Process */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Create Quote - Step {quoteStep} of 2
                </h3>
                <div className="flex gap-2 mt-2">
                  <div
                    className={`h-2 flex-1 rounded ${quoteStep >= 1 ? "bg-green-500" : "bg-gray-200"
                      }`}
                  />
                  <div
                    className={`h-2 flex-1 rounded ${quoteStep >= 2 ? "bg-green-500" : "bg-gray-200"
                      }`}
                  />
                </div>
              </div>
              <button
                onClick={handleCloseQuoteModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Step 1: Provider and ETA */}
            {quoteStep === 1 && (
              <>
                <div className="space-y-4">
                  {/* Provider - Searchable Dropdown (like EditVehicleForm) */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Provider <span className="text-red-500">*</span>
                    </label>

                    <div className="relative">
                      <input
                        type="text"
                        value={providerQuery}
                        onChange={(e) => {
                          setProviderQuery(e.target.value);
                          setIsProviderDropdownOpen(true);
                        }}
                        onFocus={() => setIsProviderDropdownOpen(true)}
                        onBlur={() => {
                          // Delay closing to allow for option clicks
                          setTimeout(
                            () => setIsProviderDropdownOpen(false),
                            200
                          );
                        }}
                        placeholder="Search or select provider..."
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        disabled={submittingQuote}
                      />

                      {/* Dropdown Arrow */}
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <ChevronDown
                          size={16}
                          className={`text-gray-400 transition-transform ${isProviderDropdownOpen ? "rotate-180" : ""
                            }`}
                        />
                      </div>
                    </div>

                    {loadingProviders && (
                      <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                        <Loader size={12} className="animate-spin" />
                        <span>Loading providers...</span>
                      </div>
                    )}

                    {/* Dropdown Options */}
                    {isProviderDropdownOpen && filteredProviders.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredProviders.map((provider) => (
                          <div
                            key={provider.id}
                            onClick={() => {
                              setSelectedProvider(provider);
                              setProviderQuery(provider.name); // Show selected text
                              setIsProviderDropdownOpen(false); // Hide dropdown
                            }}
                            className={`px-3 py-2 cursor-pointer hover:bg-green-50 transition-colors ${selectedProvider?.id === provider.id
                                ? "bg-green-100"
                                : ""
                              }`}
                          >
                            <div className="font-medium text-sm">
                              {provider.name}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {provider.lot} › {provider.stage} ›{" "}
                              {provider.stage2}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* No Results Message */}
                    {isProviderDropdownOpen &&
                      providerQuery &&
                      filteredProviders.length === 0 &&
                      !loadingProviders && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3">
                          <div className="text-sm text-gray-500 text-center">
                            No providers found for "{providerQuery}"
                          </div>
                        </div>
                      )}

                    {/* Show All Options Button */}
                    {!isProviderDropdownOpen && !providerQuery && (
                      <button
                        type="button"
                        onClick={() => {
                          setIsProviderDropdownOpen(true);
                          setProviderQuery("");
                        }}
                        className="absolute top-8 right-0 px-3 py-2 text-xs text-green-600 hover:text-green-800"
                      >
                        Show all options
                      </button>
                    )}

                    {/* Selected Provider Info */}
                    {selectedProvider && (
                      <div className="mt-2 p-2 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-xs text-green-700 font-medium">
                          Selected: {selectedProvider.name}
                        </p>
                        <p className="text-xs text-green-600">
                          Location: {selectedProvider.lot} ›{" "}
                          {selectedProvider.stage} › {selectedProvider.stage2} ›{" "}
                          {selectedProvider.name}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* ETA */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ETA <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={quoteForm.eta}
                      onChange={(e) =>
                        handleQuoteFormChange("eta", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      disabled={submittingQuote}
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseQuoteModal}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={submittingQuote}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateQuoteStep1}
                    className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    disabled={submittingQuote}
                  >
                    {submittingQuote ? (
                      <>
                        <Loader size={16} className="animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Next: Add Items"
                    )}
                  </button>
                </div>
              </>
            )}

            {/* Step 2: Quote Items */}
            {quoteStep === 2 && (
              <>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-md font-medium text-gray-800">
                      Quote Items
                    </h4>
                    <button
                      type="button"
                      onClick={handleAddQuoteItem}
                      className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                      disabled={submittingQuote}
                    >
                      + Add Item
                    </button>
                  </div>

                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {quoteItems.map((item, index) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded p-3 bg-gray-50"
                      >
                        <div className="space-y-3">
                          {/* Type Selection */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Type <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={item.type}
                              onChange={(e) =>
                                handleQuoteItemChange(
                                  index,
                                  "type",
                                  e.target.value
                                )
                              }
                              className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                              disabled={submittingQuote}
                            >
                              <option value="Labor">Labor</option>
                              <option value="Parts">Parts</option>
                            </select>
                          </div>

                          <div className="grid grid-cols-3 gap-2">
                            {/* Description */}
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Description{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) =>
                                  handleQuoteItemChange(
                                    index,
                                    "description",
                                    e.target.value
                                  )
                                }
                                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                                placeholder="e.g., Front windshield glass"
                                disabled={submittingQuote}
                              />
                            </div>

                            {/* Price */}
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Price ($){" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.price}
                                onChange={(e) =>
                                  handleQuoteItemChange(
                                    index,
                                    "price",
                                    e.target.value
                                  )
                                }
                                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                                placeholder="0.00"
                                disabled={submittingQuote}
                              />
                            </div>
                          </div>

                          {/* Remove button */}
                          {quoteItems.length > 1 && (
                            <div className="flex justify-end">
                              <button
                                type="button"
                                onClick={() => handleRemoveQuoteItem(index)}
                                className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                                disabled={submittingQuote}
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Total */}
                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">Total:</span>
                      <span className="font-bold text-green-600">
                        $
                        {quoteItems
                          .reduce(
                            (sum, item) => sum + (Number(item.price) || 0),
                            0
                          )
                          .toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseQuoteModal}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={submittingQuote}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitQuoteItems}
                    className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    disabled={submittingQuote}
                  >
                    {submittingQuote ? (
                      <>
                        <Loader size={16} className="animate-spin" />
                        Creating Items...
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        Complete Quote
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReconRequestsViewModal;
